import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import useTranslate from "../../hooks/useTranslate";
import Avatar from "../../components/common/Avatar";
import ConfirmationModal from "../../components/club/ConfirmationModal";
import LoungeHeader from "../../components/lounge/LoungeHeader";
import SortTabs from "../../components/lounge/SortTabs";
import ChannelPills from "../../components/lounge/ChannelPills";
import HouseRuleLine from "../../components/lounge/HouseRuleLine";
import LoungeComposer from "../../components/lounge/LoungeComposer";
import LoungePostCard from "../../components/lounge/LoungePostCard";
import LoungeInlineEdit from "../../components/lounge/LoungeInlineEdit";
import LoungeReportDialog from "../../components/lounge/LoungeReportDialog";
import LoungeReportedNotice from "../../components/lounge/LoungeReportedNotice";
import {
  LoungeSkeleton,
  LoungeError,
  LoungeEmpty,
  ChannelEmpty,
  LoadOlderButton,
} from "../../components/lounge/LoungeFeedStates";
import { loungeService } from "../../services/loungeService";
import { applyVote } from "../../components/lounge/voteMath";

// Agent Lounge — feed + composer (QAL-T3-FE). Design source: Agent Lounge
// .dc.html frames 1a/1b/1d/1e/1i/1j/1k/1l/1p/1r; decisions in
// GATE0_ENTSCHEIDUNGEN.md. Centered single column (680px, widening to 820px
// only from a 1536px viewport) wrapped in a 1440px app stage so the feed stays
// centered instead of swimming on very wide screens (1g); below 1536px nothing
// changes. Page ground #F8FAFC, column pattern after WhatsNew.js.
//
// T3 scope: feed (latest / top-this-week, channel filter, pagination),
// composer (create post). T4 added the comment-counter navigation to
// /lounge/:postId. T5 wired the vote pill (optimistic, server answer
// overwrites, rollback + toast on failure). T6 added the kebab flows:
// inline edit (1m·2), delete confirm (1n·2 — Delete white / Keep post blue,
// DELIBERATELY inverted, safe action primary), report dialog/sheet (1h/1k)
// with the collapse-to-confirmation + Undo (1h·2). T7 added attachments
// (composer picker + card grid/PDF row — LoungeComposer/LoungeAttachments).
// Activity data (T8) is NOT here.

const LoungeFeed = () => {
  const navigate = useNavigate();
  const { t, tv } = useTranslate();
  const user = useSelector((state) => state?.auth?.user);
  const currentAccountId = user?.account_id || user?.id || null;

  const [channels, setChannels] = useState([]);
  const [posts, setPosts] = useState([]);
  const [sort, setSort] = useState("latest");
  const [channelSlug, setChannelSlug] = useState(null); // null = All
  const [nextPage, setNextPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const highlightTimer = useRef(null);
  // Per-post click sequence — with rapid clicks only the LATEST request's
  // server answer may overwrite the optimistic state (stale answers drop).
  const voteSeq = useRef({});

  // ——— T6 moderation state ———
  const [editingId, setEditingId] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  // Posts the requester reported THIS session — drawn as the 1h·2
  // confirmation row until Undo (a reload hides them server-side, §4).
  const [reportedIds, setReportedIds] = useState(() => new Set());
  const [undoingId, setUndoingId] = useState(null);

  // Channels once on mount (pills + composer selector share the list).
  useEffect(() => {
    const ctrl = new AbortController();
    loungeService
      .getChannels(ctrl.signal)
      .then((res) => {
        if (res.success) setChannels(res.channels);
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, []);

  // Feed load — re-runs on filter/sort change (page 1).
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await loungeService.getPosts(
          { channel: channelSlug || undefined, sort, page: 1 },
          ctrl.signal,
        );
        if (ctrl.signal.aborted) return;
        if (res.success) {
          setPosts(res.results);
          setNextPage(res.next ? 2 : null);
        } else {
          setError(res.error || true);
        }
      } catch (e) {
        if (e?.name === "CanceledError" || ctrl.signal.aborted) return;
        setError(true);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [channelSlug, sort]);

  useEffect(() => () => clearTimeout(highlightTimer.current), []);

  const loadMore = useCallback(async () => {
    if (!nextPage || loadingMore) return;
    setLoadingMore(true);
    const res = await loungeService.getPosts({
      channel: channelSlug || undefined,
      sort,
      page: nextPage,
    });
    setLoadingMore(false);
    if (res.success) {
      setPosts((prev) => [...prev, ...res.results]);
      setNextPage(res.next ? nextPage + 1 : null);
    }
  }, [nextPage, loadingMore, channelSlug, sort]);

  // New post lands on top with a ~2s blue glow (1i). If a different channel
  // is filtered, the filter clears to All so the fresh post is visible.
  const handleCreated = useCallback(
    (post) => {
      if (!post) return;
      if (channelSlug && post?.channel?.slug !== channelSlug) {
        setChannelSlug(null);
      } else {
        setPosts((prev) => [post, ...prev]);
      }
      setHighlightId(post.id);
      clearTimeout(highlightTimer.current);
      highlightTimer.current = setTimeout(() => setHighlightId(null), 2000);
    },
    [channelSlug],
  );

  // Vote on a feed card (T5): optimistic score+my_vote first, the server
  // answer overwrites, a failure rolls back to the pre-click pair + toast.
  const handleVote = useCallback(
    async (post, value) => {
      const prev = { score: post.score ?? 0, my_vote: post.my_vote ?? null };
      const optimistic = applyVote(prev.score, prev.my_vote, value);
      const seq = (voteSeq.current[post.id] = (voteSeq.current[post.id] || 0) + 1);
      const patch = (fields) =>
        setPosts((list) =>
          list.map((p) => (p.id === post.id ? { ...p, ...fields } : p)),
        );
      patch(optimistic);
      const res = await loungeService.votePost(post.id, value);
      if (voteSeq.current[post.id] !== seq) return; // a newer click owns the pill
      if (res.success) {
        patch({ score: res.score, my_vote: res.my_vote });
      } else {
        patch(prev);
        toast.error(res.error || t("Something went wrong. Please try again."));
      }
    },
    [t],
  );

  // ——— T6 handlers ———

  // Inline edit save: PATCH with text + channel; the server's full post
  // object (edited_at set) replaces the list entry.
  const saveEdit = async (post, { text, channel }) => {
    setSavingEdit(true);
    const res = await loungeService.updatePost(post.id, { text, channel });
    setSavingEdit(false);
    if (res.success && res.post) {
      setPosts((list) => list.map((p) => (p.id === post.id ? res.post : p)));
      setEditingId(null);
    } else {
      toast.error(res.error || t("Something went wrong. Please try again."));
    }
  };

  // Delete (white button in the 1n·2 dialog) — soft-delete, card gone for
  // everyone.
  const performDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    const res = await loungeService.deletePost(deleteTarget.id);
    setDeleting(false);
    if (res.success) {
      setPosts((list) => list.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } else {
      setDeleteTarget(null);
      toast.error(res.error || t("Something went wrong. Please try again."));
    }
  };

  // Report submit — the card collapses to the confirmation row (1h·2).
  const submitReport = async (reason) => {
    if (!reportTarget || reportSubmitting) return;
    setReportSubmitting(true);
    const res = await loungeService.reportPost(reportTarget.id, reason);
    setReportSubmitting(false);
    if (res.success) {
      const id = reportTarget.id;
      setReportedIds((prev) => new Set(prev).add(id));
      setReportTarget(null);
    } else {
      toast.error(res.error || t("Something went wrong. Please try again."));
    }
  };

  // Undo restores the card (removes the requester's report server-side).
  const undoReport = async (postId) => {
    if (undoingId) return;
    setUndoingId(postId);
    const res = await loungeService.undoReport("post", postId);
    setUndoingId(null);
    if (res.success) {
      setReportedIds((prev) => {
        const next = new Set(prev);
        next.delete(postId);
        return next;
      });
    } else {
      toast.error(res.error || t("Something went wrong. Please try again."));
    }
  };

  const activeChannel = channels.find((c) => c.slug === channelSlug) || null;
  const feedEmpty = !loading && !error && posts.length === 0;

  return (
    <div className="w-full min-h-full bg-[#F8FAFC]">
      {/* App stage — caps the lounge at 1440px and centers it so the feed
          never swims in empty space on very wide screens (1g). The reading
          column below is 680px, widening to 820px only from 1536px. Nothing
          changes below 1536. */}
      <div className="max-w-[1440px] mx-auto">
        <div className="max-w-[680px] min-[1536px]:max-w-[820px] mx-auto px-3.5 min-[402px]:px-4 sm:px-5 pt-[18px] min-[402px]:pt-5 sm:pt-9 pb-[110px] sm:pb-14">
        <LoungeHeader />

        <div className="mt-3.5 min-[402px]:mt-4 sm:mt-5">
          <SortTabs sort={sort} onChange={setSort} />
        </div>

        <ChannelPills
          channels={channels}
          active={channelSlug}
          onSelect={setChannelSlug}
          className="mt-[11px] min-[402px]:mt-3 sm:mt-3.5"
        />

        <HouseRuleLine />

        <LoungeComposer
          channels={channels}
          currentUser={user}
          onCreated={handleCreated}
          forceExpanded={feedEmpty && !channelSlug}
        />

        <div className="mt-2.5 min-[402px]:mt-3 sm:mt-3">
          {loading ? (
            <LoungeSkeleton />
          ) : error ? (
            <LoungeError />
          ) : feedEmpty ? (
            channelSlug ? (
              <ChannelEmpty
                channelName={activeChannel?.name || channelSlug}
                onClear={() => setChannelSlug(null)}
              />
            ) : (
              <LoungeEmpty />
            )
          ) : (
            <div className="flex flex-col gap-2.5 sm:gap-3">
              {posts.map((post) =>
                reportedIds.has(post.id) ? (
                  <LoungeReportedNotice
                    key={post.id}
                    type="post"
                    onUndo={() => undoReport(post.id)}
                    undoing={undoingId === post.id}
                  />
                ) : editingId === post.id ? (
                  /* The card turns into the composer in place (1m·2). The
                     avatar column stays put on desktop; mobile edit is not
                     drawn — the editor takes the full card width there. */
                  <div
                    key={post.id}
                    className="bg-white border border-[#E4E9F0] rounded-[14px] p-[14px_15px] sm:p-[16px_18px] flex gap-2.5 sm:gap-3"
                  >
                    <div className="hidden sm:block shrink-0">
                      <Avatar
                        src={post?.author?.profile_picture_url}
                        alt={post?.author?.name || ""}
                        size={38}
                        fallbackText={post?.author?.name}
                      />
                    </div>
                    <LoungeInlineEdit
                      type="post"
                      initialText={post.text}
                      channels={channels}
                      initialChannelSlug={post?.channel?.slug || null}
                      submitting={savingEdit}
                      onCancel={() => setEditingId(null)}
                      onSave={(fields) => saveEdit(post, fields)}
                    />
                  </div>
                ) : (
                  <LoungePostCard
                    key={post.id}
                    post={post}
                    currentAccountId={currentAccountId}
                    activeChannel={channelSlug}
                    highlighted={post.id === highlightId}
                    onOpenComments={() => navigate(`/lounge/${post.id}`)}
                    onVote={(value) => handleVote(post, value)}
                    onEdit={() => setEditingId(post.id)}
                    onDelete={() => setDeleteTarget(post)}
                    onReport={() => setReportTarget(post)}
                  />
                ),
              )}
              {nextPage && <LoadOlderButton loading={loadingMore} onClick={loadMore} />}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Delete confirm (1n·2) — wording exact, buttons DELIBERATELY
          inverted: Delete = white secondary, "Keep post" = blue primary
          (safe action primary, same pattern as Discard). Centered dialog
          on mobile too (design note under 1n·2). */}
      <ConfirmationModal
        isOpen={Boolean(deleteTarget)}
        variant="v2"
        title={t("Delete this post?")}
        lines={[
          tv(
            "This removes the post and its {{count}} comments for everyone. It can't be undone.",
            { count: deleteTarget?.comment_count ?? 0 },
          ),
        ]}
        cancelText={t("Delete")}
        confirmText={t("Keep post")}
        confirmButtonClass="bg-[#2283FF] hover:bg-[#1B6FE0]"
        onClose={performDelete}
        onConfirm={() => !deleting && setDeleteTarget(null)}
      />

      {/* Report flow (1h desktop dialog / 1k mobile sheet). */}
      <LoungeReportDialog
        open={Boolean(reportTarget)}
        type="post"
        submitting={reportSubmitting}
        onCancel={() => setReportTarget(null)}
        onSubmit={submitReport}
      />
    </div>
  );
};

export default LoungeFeed;
